import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { track } from "@supersig/state";
import { SWStateRecordType } from "@superwall-me/schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { IoLanguage } from "react-icons/io5";
import { stringSimilarity } from "string-similarity-js";
import { SVariablePicker } from "../../SVariablePicker";
import STextarea from "../../Textarea";
import { SButton, SField, SIcon, SIconButton, SIconType } from "../../components";
import { LocalizationPickerRow } from "../helpers/localization";
import { useEditorAPI, useEditorDependencyInjectionContext } from "../providers";
const LocalizationControllerInner = track(function({ value, onChangeValue, isDynamic, onClickValue, onPopoverCallback, localization }) {
    const resources = localization.getResources();
    const localizationItems = useMemo(()=>{
        return resources.map((x)=>{
            return {
                key: x.id,
                name: `${x.props.files.find((x)=>true)?.keyName || x.id}:${x.props.text.value}`,
                distance: (value?.value ? stringSimilarity(value?.value || "", x.props.text.value) : 0) - (x.props.isDeleted == true ? 1 : 0) * 1.0,
                state: x
            };
        }).sort((a, b)=>b.distance - a.distance);
        return [];
    }, [
        resources,
        value
    ]);
    const resource = useMemo(()=>{
        return value?.resourceReference ? resources.find((x)=>x.id === value.resourceReference?.resourceId) : undefined;
    }, [
        resources,
        value
    ]);
    const hasLocalization = useMemo(()=>{
        return value?.resourceReference && resources.find((x)=>x.id === value.resourceReference?.resourceId);
    }, [
        value,
        resources
    ]);
    return /*#__PURE__*/ _jsx(SVariablePicker, {
        items: localizationItems,
        asButton: true,
        disableFuzzySearch: true,
        menuWidth: 300,
        direction: "left",
        didSelect: (item)=>{
            onChangeValue && onChangeValue({
                ...value,
                ...item.state.props.text,
                resourceReference: {
                    resourceId: item.state?.id,
                    version: item.state.version
                }
            });
        },
        didClose: ()=>{},
        onAdd: ({ setIsOpen })=>{
            if (setIsOpen) {
                setIsOpen(false);
            }
            console.log(`calling open !!`);
            localization.openMenu({
                startingValue: value?.value,
                showSimilarResources: false
            }, onPopoverCallback);
        },
        addButtonRender: ()=>{
            return /*#__PURE__*/ _jsx(SIcon, {
                size: 13,
                type: "plus"
            });
        },
        render: (item)=>{
            return /*#__PURE__*/ _jsx(LocalizationPickerRow, {
                item: item
            });
        },
        buttonRender: (props)=>{
            return /*#__PURE__*/ _jsx("div", {
                className: "flex flex-row items-center  gap-2 text-brand-primary",
                children: /*#__PURE__*/ _jsx(SButton, {
                    className: `!h-[24px] !p-0 !m-0 !transition-transform flex flex-row items-center `,
                    isMinimal: true,
                    onClick: ()=>{
                        props.setIsOpen(!props.isOpen);
                    },
                    children: hasLocalization ? /*#__PURE__*/ _jsxs("div", {
                        className: "text-[13px] !font-normal rounded-md  px-2 py-1 overflow-hidden flex flex-row items-center border border-brand-primary/10 bg-brand-primary bg-opacity-7  text-brand-primary flex flex-row gap-2 items-center justify-center",
                        children: [
                            /*#__PURE__*/ _jsx(IoLanguage, {
                                size: 14
                            }),
                            resource?.props?.files.find((x)=>true)?.keyName || resource?.props.keyId
                        ]
                    }) : /*#__PURE__*/ _jsxs("span", {
                        className: "flex flex-row items-center pl-1 gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(IoLanguage, {
                                size: 16
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "",
                                children: "Localize"
                            })
                        ]
                    })
                })
            });
        }
    });
});
const LocalizationController = (props)=>{
    const { localization: { getLocalizationType, useLocalization } } = useEditorAPI();
    const localization = useLocalization();
    const localizationType = getLocalizationType();
    if (localizationType === "SIMPLE") {
        return null;
    }
    if (localization.status === "loading") {
        return null;
    }
    // All the user to setup localization
    if (localization.status === "error") {
        return /*#__PURE__*/ _jsxs("span", {
            className: "flex flex-row items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(IoLanguage, {
                    size: 14
                }),
                " Unexpected localization error: ",
                localization.error
            ]
        });
    }
    if (localization.status === "hidden") {
        return null;
    }
    // All the user to setup localization
    if (localization.status === "not-setup") {
        const { setupURL } = localization;
        return /*#__PURE__*/ _jsx(SButton, {
            className: "!h-[24px] !py-0 !pl-0",
            isMinimal: true,
            onClick: ()=>{
                if (typeof window !== "undefined") {
                    window.open(setupURL, "_blank");
                }
            },
            children: /*#__PURE__*/ _jsxs("span", {
                className: "flex flex-row items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(IoLanguage, {
                        size: 14
                    }),
                    " Setup Localization ",
                    /*#__PURE__*/ _jsx(FiExternalLink, {
                        size: 14
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(LocalizationControllerInner, {
        ...props,
        localization: localization
    });
};
export const TextPropertyEditor = ({ readOnly, value, onChangeValue, isDynamic, onClickValue, disableLocalization = false })=>{
    const { useVariablePickerItems, requiresLiquid, getRequiredPaths } = useEditorDependencyInjectionContext();
    const { localization: { useLocalization, applyResourceToLiteral } } = useEditorAPI();
    const localization = useLocalization();
    const onTextChange = useCallback((newText, previousText)=>{
        // Look for valid liquid string
        if (requiresLiquid(newText)) {
            const requiredRecords = getRequiredPaths(newText).map((x)=>SWStateRecordType.createId(x));
            onChangeValue && onChangeValue({
                ...value,
                type: "property-text",
                value: newText,
                rendering: {
                    type: "liquid",
                    requiredStateIds: requiredRecords
                }
            });
        } else {
            onChangeValue && onChangeValue({
                ...value,
                type: "property-text",
                value: newText,
                rendering: {
                    type: "literal"
                }
            });
        }
    }, [
        onChangeValue,
        value
    ]);
    const popoverCallbackRef = useRef(null);
    const onPopoverCallback = useCallback((props)=>{
        if (popoverCallbackRef.current) {
            popoverCallbackRef.current(props);
        }
    }, []);
    useEffect(()=>{
        const callback = (response)=>{
            if (response && response.type === "edit-text-resource") {
                const resource = response.resource;
                if (!resource || !value) {
                    return;
                }
                const updatedLiteral = applyResourceToLiteral({
                    ...value,
                    // Force the version to -1 to ensure the latest version is used
                    resourceReference: {
                        resourceId: resource.id,
                        version: -1
                    }
                }, resource);
                updatedLiteral && onChangeValue && onChangeValue(updatedLiteral);
            }
        };
        popoverCallbackRef.current = callback;
    }, [
        value,
        onChangeValue,
        applyResourceToLiteral
    ]);
    const items = useVariablePickerItems();
    const textValue = value?.value || "";
    const [hasFocus, setHasFocus] = useState(false);
    const textAreaRef = useRef(null);
    const resources = localization.status === "ready" ? localization.getResources() || [] : [];
    const resource = useMemo(()=>{
        return resources.find((x)=>x.id === value?.resourceReference?.resourceId);
    }, [
        resources,
        value
    ]);
    const [isHoveringLock, setIsHoveringLock] = useState(false);
    return /*#__PURE__*/ _jsx("div", {
        onClick: onClickValue,
        className: isDynamic ? "cursor-pointer" : "",
        children: /*#__PURE__*/ _jsx("div", {
            className: isDynamic ? "pointer-events-none" : "",
            children: /*#__PURE__*/ _jsxs(SField, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        onClick: ()=>{
                            if (resource && localization.status === "ready") {
                                localization.openMenu({
                                    resource: resource
                                }, onPopoverCallback);
                            }
                        },
                        children: /*#__PURE__*/ _jsx(STextarea, {
                            ref: textAreaRef,
                            onFocus: (e)=>{
                                if (resource) {
                                    e.preventDefault();
                                    textAreaRef.current?.blur();
                                    return;
                                }
                                e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length);
                                setHasFocus(true);
                            },
                            onBlur: (e)=>{
                                setTimeout(()=>{
                                    setHasFocus(false);
                                }, 1000);
                            },
                            className: `!my-0 !resize-none`,
                            value: textValue,
                            autoFocus: !isDynamic,
                            onChange: (e)=>{
                                if (readOnly || resource) return;
                                onTextChange(e.target.value, textValue);
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: `w-full flex flex-row pt-1 pr-1 pl-0 pb-0 group  ${disableLocalization || localization.status === "loading" || localization.status === "hidden" ? "justify-end" : "justify-between"}`,
                        children: [
                            !readOnly && !disableLocalization && /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-row items-center justify-center gap-0",
                                children: [
                                    /*#__PURE__*/ _jsx(LocalizationController, {
                                        value: value,
                                        onChangeValue: onChangeValue,
                                        isDynamic: isDynamic,
                                        onClickValue: onClickValue,
                                        onPopoverCallback: onPopoverCallback
                                    }),
                                    resource && /*#__PURE__*/ _jsx(_Fragment, {
                                        children: /*#__PURE__*/ _jsx(SIconButton, {
                                            icon: SIconType.Trash,
                                            iconSize: 12,
                                            className: "!p-0 !m-0 !h-auto opacity-0 group-hover:opacity-100",
                                            isMinimal: true,
                                            onClick: ()=>{
                                                onChangeValue && value && onChangeValue({
                                                    ...value,
                                                    resourceReference: undefined
                                                });
                                            }
                                        })
                                    })
                                ]
                            }),
                            !readOnly && /*#__PURE__*/ _jsx(SVariablePicker, {
                                items: items,
                                asButton: true,
                                enableHotkey: hasFocus,
                                didSelect: (item)=>{
                                    // onTextChange(`${textValue} ${item.liquid?.trim()} `)
                                    let text = textValue;
                                    // insert at cursor
                                    if (textAreaRef.current) {
                                        const { selectionStart, selectionEnd } = textAreaRef.current;
                                        const beforeText = text.substring(0, selectionStart).trimEnd();
                                        const textToInsert = item.liquid?.trim() || "";
                                        const rhs = text.substring(selectionEnd, text.length).trimStart();
                                        const lhs = `${beforeText} ${textToInsert} `;
                                        const newPosition = lhs.length;
                                        text = `${lhs}${rhs}`;
                                        onTextChange(text, textValue);
                                        setTimeout(()=>{
                                            textAreaRef.current?.focus();
                                            // set the cursor to the end of the inserted text
                                            if (textAreaRef.current) {
                                                textAreaRef.current.selectionStart = newPosition;
                                                textAreaRef.current.selectionEnd = newPosition;
                                            }
                                        }, 100);
                                    } else {
                                        onTextChange(`${textValue} ${item.liquid?.trim()} `, textValue);
                                    }
                                },
                                didClose: ()=>{
                                    setTimeout(()=>{
                                        textAreaRef.current?.focus();
                                    }, 100);
                                }
                            })
                        ]
                    })
                ]
            })
        })
    });
};
